/**
 * "What the village costs" — the contract as entered, on the dark tile every module's plan summary
 * carries. It is the CONTRACT, not a plan: it appears the moment the figures are typed and it does
 * not change with the funding chosen (owner, 24 Sep 2026).
 *
 * THE DEPARTURE FEE AS A LADDER. "Years in village" used to do two jobs: set how much departure fee
 * had accrued, and tell the projection to sell the unit in that year and carry on with the client
 * living nowhere. The default is now "for the rest of my life" — no exit inside the projection, the
 * contract carried in net worth as the accrued entitlement — and the question the field was really
 * there to answer is answered here instead: what would come back if the client left after 3, 5 or
 * 10 years. With a set number of years chosen, that year's figure is shown and the tile says the
 * projection sells the unit then.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { computeExitEntitlement } from '../../../../shared/src/calculation/retirementVillageCalculations.ts';
import type { VillageInputs } from './RetirementVillagePage.tsx';

export const DEPARTURE_LADDER_YEARS = [3, 5, 10] as const;

/** What comes back after `years` in the village, on the contract as entered. */
export function exitAfter(villageInputs: VillageInputs, years: number): { dmf: number; netToResident: number } {
    const exit = computeExitEntitlement(
        villageInputs.entryFee || 0,
        years,
        villageInputs.growthRatePct || 0,
        villageInputs.capitalGainShareToResident ?? 0.5,
        { dmfPctPerYear: villageInputs.dmfPctPerYear || 0, dmfCapYears: villageInputs.dmfCapYears || 0, dmfBasis: 'entry' },
        villageInputs.reinstatementCap || 0,
    );
    return { dmf: exit.dmfAmount, netToResident: exit.netToResident };
}

interface RvVillageCostsTileProps {
    villageInputs: VillageInputs;
    annualOngoing: number;
    /** Anything the caller wants under the rows — the funding note, the shortfall ledger. */
    children?: React.ReactNode;
}

const Row = ({ label, value, tone }: { label: string; value: string; tone?: 'good' }) => (
    <div className="flex justify-between gap-3">
        <span className="text-gray-300">{label}</span>
        <span className={`font-semibold tabular-nums ${tone === 'good' ? 'text-green-400' : ''}`}>{value}</span>
    </div>
);

const RvVillageCostsTile = ({ villageInputs, annualOngoing, children }: RvVillageCostsTileProps) => {
    const entryFee = villageInputs.entryFee || 0;
    const forLife = !(villageInputs.yearsResidency > 0);
    const chosenYears = Math.max(1, Math.floor(villageInputs.yearsResidency || 0));
    return (
        <div className="bg-gray-800 text-white rounded-xl p-4">
            <div className="space-y-3">
                <Row label="Entry payment" value={formatCurrency(entryFee)} />
                <Row label="Annual ongoing fees" value={formatCurrency(annualOngoing)} />
                {entryFee > 0 && (
                    forLife ? (
                        <div className="pt-3 border-t border-gray-600">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">If you left the village</p>
                            <div className="space-y-1.5">
                                {DEPARTURE_LADDER_YEARS.map((y) => {
                                    const e = exitAfter(villageInputs, y);
                                    return (
                                        <div key={y} className="flex justify-between gap-3 text-sm">
                                            <span className="text-gray-300">after {y} years</span>
                                            <span className="tabular-nums">
                                                <span className="text-gray-400">fee {formatCurrency(e.dmf)} · </span>
                                                <span className="font-semibold text-green-400">{formatCurrency(e.netToResident)} back</span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-gray-400 mt-2">Modelled as staying for life: no sale is assumed, and what the contract is worth is carried in the estate.</p>
                        </div>
                    ) : (
                        <>
                            <Row label={`Departure fee after ${chosenYears} ${chosenYears === 1 ? 'year' : 'years'} (est.)`} value={formatCurrency(exitAfter(villageInputs, chosenYears).dmf)} />
                            <Row label="You get back (est.)" value={formatCurrency(exitAfter(villageInputs, chosenYears).netToResident)} tone="good" />
                            <p className="text-xs text-gray-400 pt-1">The projection sells the unit in year {chosenYears} and stops the fees. Where you live after that is not modelled.</p>
                        </>
                    )
                )}
            </div>
            {children}
        </div>
    );
};

export default RvVillageCostsTile;
